import { useState, useEffect } from 'react';
import { getImageFileSize, formatBytes, calculateReduction } from '../utils';
import { useLanguage } from '../context/LanguageContext';

interface FileSizeOverlayProps {
  originalUrl: string;
  optimizedUrl: string;
  isOptimized?: boolean;
  width?: number;
  height?: number;
}

const FileSizeOverlay: React.FC<FileSizeOverlayProps> = ({ 
  originalUrl, 
  optimizedUrl, 
  isOptimized = false,
  width,
  height
}) => {
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [optimizedSize, setOptimizedSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchSizes = async () => {
      setLoading(true);
      console.log('--------------------------------');
      console.log(`FileSizeOverlay: Comparing sizes for image displayed at ${width}x${height}:`);
      console.log(`Original URL: ${originalUrl}`);
      console.log(`Optimized URL: ${optimizedUrl}`);
      
      try {
        const [origSize, optSize] = await Promise.all([
          getImageFileSize(originalUrl, null, null), // No dimensions for original URL
          getImageFileSize(optimizedUrl, width, height) // Only apply dimensions to optimized URL
        ]);
        
        setOriginalSize(origSize);
        setOptimizedSize(optSize);
        
        // Log the comparison results
        if (origSize && optSize) {
          const reduction = calculateReduction(origSize, optSize);
          console.log(`Comparison results:`);
          console.log(`Original (full size): ${formatBytes(origSize)}`);
          console.log(`Optimized (${width}x${height}): ${formatBytes(optSize)}`);
          console.log(`Reduction: ${reduction}%`);
        }
      } catch (error) {
        console.error('Error fetching image sizes:', error);
      } finally {
        setLoading(false);
      }
      console.log('--------------------------------');
    };

    fetchSizes();
  }, [originalUrl, optimizedUrl, width, height]);
  
  if (loading) {
    return (
      <div className="absolute bottom-2 left-2 bg-gray-900 bg-opacity-80 text-white text-xs p-1 rounded">
        {t('file_size_loading')}
      </div>
    );
  }
  
  if (originalSize === null || optimizedSize === null) {
    return null;
  }
  
  const reduction = calculateReduction(originalSize, optimizedSize);
  
  // Simplified format that takes up less space
  return (
    <div className="absolute bottom-2 left-2 bg-gray-900 bg-opacity-80 text-white text-xs p-2 rounded">
      {isOptimized && originalSize !== optimizedSize ? (
        <div>
          <span className="font-semibold">{t('file_size')}:</span> {formatBytes(originalSize)}
          <span className="text-gray-400 mx-1">→</span>
          <span className="text-green-400">
            {formatBytes(optimizedSize)} ({reduction}% {t('file_size_saved')})
          </span>
        </div>
      ) : (
        <div>
          <span className="font-semibold">{t('file_size')}:</span> {formatBytes(originalSize)}
        </div>
      )}
    </div>
  );
};

export default FileSizeOverlay;